/**
 * Clinic Slider Module
 * SPでは全クリニックをループ表示し、先頭スライドに応じてタブを更新
 */
export class ClinicTabs {
  constructor() {
    this.root = document.querySelector('.l-clinic');
    this.isInitialized = false;

    if (!this.root) {
      return;
    }

    this.tabs = Array.from(this.root.querySelectorAll('.l-clinic__tab-item'));
    this.items = Array.from(this.root.querySelectorAll('.l-clinic__item'));
    this.viewport = this.root.querySelector('.l-clinic__viewport');
    this.track = this.root.querySelector('.l-clinic__track');
    this.list = this.root.querySelector('.l-clinic__list');
    this.indicatorThumb = this.root.querySelector('.l-clinic__indicator-thumb');
    this.pcMedia = window.matchMedia('(min-width: 768px)');
    this.areaCounts = this.createAreaCounts();
    this.resizeRaf = null;
    this.splideRetryTimer = null;
    this.splideRetryCount = 0;
    this.splideMaxRetries = 20;
    this.splide = null;
    this.preventClickTimer = null;
    this.wheelTimer = null;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.mouseStartX = 0;
    this.mouseStartY = 0;
    this.isMouseDown = false;
    this.shouldPreventClick = false;
    this.onTrackTouchStart = this.handleTrackTouchStart.bind(this);
    this.onTrackTouchEnd = this.handleTrackTouchEnd.bind(this);
    this.onTrackMouseDown = this.handleTrackMouseDown.bind(this);
    this.onWindowMouseUp = this.handleWindowMouseUp.bind(this);
    this.onTrackClick = this.handleTrackClick.bind(this);
    this.onTrackWheel = this.handleTrackWheel.bind(this);

    this.onResize = this.handleResize.bind(this);

    if (this.tabs.length === 0 || !this.viewport || !this.track || !this.list || this.items.length === 0) {
      return;
    }

    this.updateTabCounts();
    this.setupTabs();
    this.bindEvents();
    this.syncMode();
    this.isInitialized = true;
  }

  bindEvents() {
    window.addEventListener('resize', this.onResize);
  }

  destroy() {
    if (!this.isInitialized) {
      return;
    }

    window.removeEventListener('resize', this.onResize);

    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = null;
    }

    this.clearPreventClickTimer();
    this.destroySlider();
  }

  setupTabs() {
    this.tabs.forEach((tab) => {
      if (tab.classList.contains('is-active')) {
        tab.setAttribute('aria-current', 'true');
        return;
      }

      tab.removeAttribute('aria-current');
    });
  }

  createAreaCounts() {
    return this.items.reduce((counts, item) => {
      const { area } = item.dataset;

      if (!area) {
        return counts;
      }

      counts[area] = (counts[area] || 0) + 1;
      return counts;
    }, {});
  }

  updateTabCounts() {
    this.tabs.forEach((tab) => {
      const area = tab.dataset.tab;
      const countEl = tab.querySelector('.l-clinic__tab-count');

      if (!countEl) {
        return;
      }

      countEl.textContent = String(this.areaCounts[area] || 0);
    });
  }

  handleResize() {
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
    }

    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = null;
      this.syncMode();
    });
  }

  syncMode() {
    if (this.pcMedia.matches) {
      this.destroySlider();
      this.root.classList.remove('is-scrollable');
      return;
    }

    this.initSlider();
  }

  initSlider() {
    if (this.splide) {
      this.refreshFixedWidth();
      this.updateActiveTabByIndex(this.splide.index);
      this.updateIndicator();
      this.root.classList.add('is-scrollable');
      return;
    }

    if (typeof Splide === 'undefined') {
      if (!this.splideRetryTimer && this.splideRetryCount < this.splideMaxRetries) {
        this.splideRetryCount += 1;
        this.splideRetryTimer = window.setTimeout(() => {
          this.splideRetryTimer = null;
          this.syncMode();
        }, 100);
      }
      return;
    }

    this.setupSliderMarkup();
    this.splideRetryCount = 0;
    const gap = window.getComputedStyle(this.root).getPropertyValue('--clinic-slider-gap').trim() || '0px';
    const slideWidth = this.items[0]?.getBoundingClientRect().width || 0;

    this.splide = new Splide(this.viewport, {
      type: 'loop',
      arrows: false,
      pagination: false,
      fixedWidth: slideWidth ? `${slideWidth}px` : null,
      perMove: 1,
      gap,
      drag: false,
      snap: true,
      focus: 0,
      trimSpace: false,
      updateOnMove: true,
      autoplay: false,
      speed: 300,
    });

    this.splide.on('mounted moved', () => {
      this.updateActiveTabByIndex(this.splide.index);
      this.updateIndicator();
    });

    this.splide.mount();
    this.track.addEventListener('touchstart', this.onTrackTouchStart, { passive: true });
    this.track.addEventListener('touchend', this.onTrackTouchEnd, { passive: true });
    this.track.addEventListener('mousedown', this.onTrackMouseDown);
    this.track.addEventListener('click', this.onTrackClick, true);
    this.track.addEventListener('wheel', this.onTrackWheel, { passive: false });
    window.addEventListener('mouseup', this.onWindowMouseUp);
    this.root.classList.add('is-scrollable');
  }

  refreshFixedWidth() {
    if (!this.splide) {
      return;
    }

    const slideWidth = this.items[0]?.getBoundingClientRect().width || 0;
    if (!slideWidth) {
      return;
    }

    this.splide.options = {
      fixedWidth: `${slideWidth}px`,
    };
    this.splide.refresh();
  }

  setupSliderMarkup() {
    this.viewport.classList.add('splide');
    this.track.classList.add('splide__track');
    this.list.classList.add('splide__list');
    this.items.forEach((item) => {
      item.classList.add('splide__slide');
      item.removeAttribute('hidden');
    });
  }

  destroySlider() {
    if (!this.viewport || !this.track || !this.list) {
      return;
    }

    if (this.splideRetryTimer) {
      window.clearTimeout(this.splideRetryTimer);
      this.splideRetryTimer = null;
    }
    this.splideRetryCount = 0;

    this.clearPreventClickTimer();

    if (this.splide) {
      this.splide.destroy();
      this.splide = null;
    }

    this.viewport.classList.remove('splide');
    this.track.classList.remove('splide__track');
    this.list.classList.remove('splide__list');
    this.track.removeEventListener('touchstart', this.onTrackTouchStart);
    this.track.removeEventListener('touchend', this.onTrackTouchEnd);
    this.track.removeEventListener('mousedown', this.onTrackMouseDown);
    this.track.removeEventListener('click', this.onTrackClick, true);
    this.track.removeEventListener('wheel', this.onTrackWheel);
    window.removeEventListener('mouseup', this.onWindowMouseUp);
    this.isMouseDown = false;
    this.shouldPreventClick = false;
    this.items.forEach((item) => {
      item.classList.remove('splide__slide');
      item.removeAttribute('style');
      item.removeAttribute('aria-hidden');
    });

    if (this.indicatorThumb) {
      this.indicatorThumb.style.width = '100%';
      this.indicatorThumb.style.left = '0';
    }

    if (this.wheelTimer) {
      window.clearTimeout(this.wheelTimer);
      this.wheelTimer = null;
    }
  }

  updateActiveTabByIndex(index) {
    const activeItem = this.items[index];
    const area = activeItem?.dataset.area;

    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.tab === area;
      tab.classList.toggle('is-active', isActive);
      if (isActive) {
        tab.setAttribute('aria-current', 'true');
        return;
      }

      tab.removeAttribute('aria-current');
    });
  }

  updateIndicator() {
    if (!this.splide || !this.indicatorThumb) {
      return;
    }

    const total = Math.max(this.items.length, 1);
    const currentIndex = this.splide.index % total;
    const filledWidth = ((currentIndex + 1) / total) * 100;

    this.indicatorThumb.style.width = `${filledWidth}%`;
    this.indicatorThumb.style.left = '0';
  }

  handleTrackTouchStart(event) {
    const touch = event.changedTouches?.[0];
    if (!touch) {
      return;
    }

    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  handleTrackTouchEnd(event) {
    if (!this.splide) {
      return;
    }

    const touch = event.changedTouches?.[0];
    if (!touch) {
      return;
    }

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    const swipeThreshold = 30;

    if (Math.abs(deltaX) <= swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    this.preventNextClick();
    this.splide.go(deltaX < 0 ? '>' : '<');
  }

  handleTrackMouseDown(event) {
    if (event.button !== 0) {
      return;
    }

    this.isMouseDown = true;
    this.mouseStartX = event.clientX;
    this.mouseStartY = event.clientY;
    this.shouldPreventClick = false;
  }

  handleWindowMouseUp(event) {
    if (!this.splide || !this.isMouseDown) {
      return;
    }

    this.isMouseDown = false;

    const deltaX = event.clientX - this.mouseStartX;
    const deltaY = event.clientY - this.mouseStartY;
    const swipeThreshold = 30;

    if (Math.abs(deltaX) <= swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    this.preventNextClick();
    this.splide.go(deltaX < 0 ? '>' : '<');
  }

  handleTrackClick(event) {
    if (!this.shouldPreventClick) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.shouldPreventClick = false;
    this.clearPreventClickTimer();
  }

  handleTrackWheel(event) {
    if (!this.splide || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
      return;
    }

    event.preventDefault();

    if (Math.abs(event.deltaX) < 24) {
      return;
    }

    if (this.wheelTimer) {
      window.clearTimeout(this.wheelTimer);
      this.wheelTimer = window.setTimeout(() => {
        this.wheelTimer = null;
      }, 120);
      return;
    }

    this.splide.go(event.deltaX > 0 ? '>' : '<');
    this.wheelTimer = window.setTimeout(() => {
      this.wheelTimer = null;
    }, 120);
  }

  preventNextClick() {
    this.shouldPreventClick = true;
    this.clearPreventClickTimer();
    this.preventClickTimer = window.setTimeout(() => {
      this.shouldPreventClick = false;
      this.preventClickTimer = null;
    }, 150);
  }

  clearPreventClickTimer() {
    if (!this.preventClickTimer) {
      return;
    }

    window.clearTimeout(this.preventClickTimer);
    this.preventClickTimer = null;
  }
}
